import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// During beta, treat every user as Pro with unlimited access.
// Remove REACT_APP_BETA_MODE (or set to 'false') to enforce the paywall.
const BETA_MODE = process.env.REACT_APP_BETA_MODE === 'true';

/**
 * Hook to manage subscription state.
 * Fetches status from backend and exposes upgrade helpers.
 *
 * Returns:
 *   isPro          — boolean, true if active or trialing
 *   status         — "free" | "trialing" | "active" | "past_due" | "cancelled"
 *   meditationsLeft — number remaining on free plan (null = unlimited)
 *   loading        — boolean
 *   refresh        — function to re-fetch status
 *   startCheckout  — async (priceId) → opens Stripe Checkout
 *   openPortal     — async () → opens Stripe Customer Portal
 */
export function useSubscription(userId) {
  const [data, setData] = useState({
    isPro: false,
    status: 'free',
    meditationsCreated: 0,
    meditationsLimit: 2,
    loading: true,
  });

  const fetch = useCallback(async () => {
    if (!userId) return;
    try {
      const res = await axios.get(`${API}/payments/status/${userId}`);
      const d = res.data;
      setData({
        isPro: d.is_pro,
        status: d.status,
        meditationsCreated: d.meditations_created ?? 0,
        meditationsLimit: d.meditations_limit ?? 2,
        loading: false,
      });
    } catch {
      setData(prev => ({ ...prev, loading: false }));
    }
  }, [userId]);

  useEffect(() => { fetch(); }, [fetch]);

  // Re-check after returning from Stripe (success param in URL)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('payment') === 'success') {
      setTimeout(fetch, 2000); // give webhook time to process
    }
  }, [fetch]);

  const startCheckout = async (priceId) => {
    try {
      const res = await axios.post(`${API}/payments/create-checkout`, null, {
        params: { user_id: userId, price_id: priceId }
      });
      window.location.href = res.data.checkout_url;
    } catch (err) {
      console.error('Checkout error:', err);
      throw err;
    }
  };

  const openPortal = async () => {
    try {
      const res = await axios.post(`${API}/payments/portal`, null, {
        params: { user_id: userId }
      });
      window.location.href = res.data.portal_url;
    } catch (err) {
      console.error('Portal error:', err);
      throw err;
    }
  };

  const meditationsLeft = data.meditationsLimit !== null
    ? Math.max(0, data.meditationsLimit - data.meditationsCreated)
    : null;

  if (BETA_MODE) {
    return {
      isPro: true,
      status: 'active',
      meditationsCreated: data.meditationsCreated,
      meditationsLimit: null,
      meditationsLeft: null,
      loading: data.loading,
      refresh: fetch,
      startCheckout,
      openPortal,
    };
  }

  return {
    ...data,
    meditationsLeft,
    refresh: fetch,
    startCheckout,
    openPortal,
  };
}
